// Facturación clásica (venta). Financiero → Gestión CxC → Facturación.
// Consume el catálogo de inventario y resolver_precio (precio según cliente/convenio),
// graba con fac_crear_factura (origen='facturacion') y reutiliza el pipeline SRI.
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, Loader2, Pencil, Plus, Trash2, FileText, Download, Receipt } from 'lucide-react';
import { BuscadorCombo } from '@/components/ui/BuscadorCombo';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { DescartarDialog, type DocDescartable } from './DescartarDialog';
import { AnularDialog, type DocAnulable } from './AnularDialog';
import { usePermissions } from '@/lib/hooks/usePermissions';
import { PagosEditor, type PagoCab } from './PagosEditor';
import { abrirFacturaPdf } from './facturaPdf';
import { ReembolsoModal } from './ReembolsoModal';
import { fechaLocal } from '@/lib/utils/fecha';
import { labelCentroCosto, useCentrosContables, useCentroCostoModo } from './centroCosto';
import { AvisoRenombre } from '@/components/ui/AvisoRenombre';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { LOCALE } from '@/lib/utils/regional';

const FIN = { headers: { 'Content-Profile': 'financiero', 'Accept-Profile': 'financiero' } };
const INV = { headers: { 'Content-Profile': 'inventario', 'Accept-Profile': 'inventario' } };
const field = 'flex h-9 w-full rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
const usd = (v: number) => v.toLocaleString(LOCALE, { style: 'currency', currency: 'USD' });
const hoy = fechaLocal();
const urlDoc = (r: string | null | undefined) => (r ? r.replace(/\/opt\/(?:sri\/documentos|plataforma\/sri-docs)/, '/sri-docs') : null);

const ESTADO: Record<string, { label: string; cls: string }> = {
  por_emitir: { label: 'Por emitir', cls: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' },
  encolado: { label: 'Encolado', cls: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
  procesando: { label: 'Procesando', cls: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
  en_procesamiento: { label: 'En procesamiento (SRI)', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' },
  detenido: { label: 'Detenido', cls: 'bg-muted text-muted-foreground' },
  autorizado: { label: 'Autorizado', cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' },
  devuelto: { label: 'Devuelto', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  rechazado: { label: 'Rechazado', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  error: { label: 'Error', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  anulado: { label: 'Anulado', cls: 'bg-muted text-muted-foreground line-through' },
  descartado: { label: 'Descartado', cls: 'bg-muted text-muted-foreground' },
};
const EMITIBLE = ['por_emitir', 'rechazado', 'devuelto', 'error'];
// Filtro «Estado» de la pestaña Facturas: grupos + cada estado individual.
const GRUPOS_ESTADO: Record<string, { t: string; estados: string[] }> = {
  'g:atencion': { t: 'Requieren atención (por emitir, rechazadas, devueltas, error)', estados: EMITIBLE },
  'g:proceso': { t: 'En proceso de envío al SRI', estados: ['encolado', 'procesando', 'en_procesamiento'] },
  'g:vigentes': { t: 'Vigentes (todo menos anuladas y descartadas)', estados: Object.keys(ESTADO).filter((k) => !['anulado', 'descartado'].includes(k)) },
};
const cumpleEstado = (estado: string, filtro: string) =>
  !filtro || (filtro.startsWith('g:') ? (GRUPOS_ESTADO[filtro]?.estados ?? []).includes(estado) : estado === filtro);

interface PartnerOpt { id: string; razon_social: string; identificacion: string | null; tipo_partner: string[] }
// El catálogo facturable puede venir de dos fuentes: los servicios de
// inventario y, si la empresa está marcada como centro médico, los exámenes
// (script 857). `origen` decide si el precio se resuelve por lista o es el del
// examen — `resolver_precio` sólo conoce productos de inventario.
interface ProductoOpt {
  id: string; codigo: string; nombre: string; iva_tarifa: number; precio_base: number;
  origen: 'producto' | 'examen'; grupo: string;
}
interface Linea {
  key: number; producto_id: string; codigo: string; descripcion: string;
  cantidad: number; precio_unitario: number; descMode: 'pct' | 'fijo'; descVal: number;
  iva_tarifa: number; proyecto_id: string;
}

let _k = 1;
const nuevaLinea = (proyecto = ''): Linea => ({
  key: _k++, producto_id: '', codigo: '', descripcion: '', cantidad: 1, precio_unitario: 0,
  descMode: 'pct', descVal: 0, iva_tarifa: 15, proyecto_id: proyecto,
});
const descUnit = (l: Linea) => (l.descMode === 'pct' ? l.precio_unitario * (l.descVal || 0) / 100 : (l.descVal || 0));
const baseLinea = (l: Linea) => l.cantidad * (l.precio_unitario - descUnit(l));
const totalLinea = (l: Linea) => baseLinea(l) * (1 + (l.iva_tarifa || 0) / 100);

export function Facturacion() {
  const qc = useQueryClient();
  const [vista, setVista] = useState<'nueva' | 'emitidas'>('nueva');
  const [editId, setEditId] = useState<string | null>(null);
  return (
    <div className="p-6 space-y-4">
      <AvisoRenombre clave="emitir-factura" antes="Facturación x Servicios" />
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Emitir Factura</h1>
        <div className="flex gap-1 rounded-lg border p-1">
          {([['nueva', editId ? 'Editar factura' : 'Nueva factura'], ['emitidas', 'Facturas']] as const).map(([k, t]) => (
            <button key={k} onClick={() => { if (k === 'nueva' && vista === 'emitidas') setEditId(null); setVista(k); }}
              className={`rounded-md px-3 py-1 text-sm ${vista === k ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>{t}</button>
          ))}
        </div>
      </div>
      {vista === 'nueva'
        ? <NuevaFactura editId={editId} onCreada={() => { setEditId(null); setVista('emitidas'); void qc.invalidateQueries({ queryKey: ['fac-facturas'] }); }} onCancelar={editId ? () => { setEditId(null); setVista('emitidas'); } : undefined} />
        : <Emitidas onEdit={(id) => { setEditId(id); setVista('nueva'); }} />}
    </div>
  );
}

export function NuevaFactura({ editId, onCreada, onCancelar }: { editId: string | null; onCreada: () => void; onCancelar?: () => void }) {
  const { data: partners = [] } = useQuery<PartnerOpt[]>({
    queryKey: ['fin-partners'], queryFn: async () => (await apiClient.post('/rpc/sri_partners', {}, FIN)).data || [],
  });
  // Facturables: clientes y pacientes (la facturación especializada factura a pacientes
  // del centro médico; sin esto, al editar una de esas el combo no precargaba al receptor)
  const clientes = useMemo(() => partners.filter((p) => (p.tipo_partner || []).includes('cliente') || (p.tipo_partner || []).includes('paciente')), [partners]);
  const centros = useMemo(() => partners.filter((p) => (p.tipo_partner || []).includes('centro_costo')), [partners]);
  // Combo buscable de proyecto (cabecera y líneas): identificación + nombre
  const itemsProyecto = useMemo(() => partners.map((p) => ({ id: p.id, codigo: p.identificacion ?? '', nombre: p.razon_social })), [partners]);
  // Clientes PRIMERO (al abrir el combo se ven ellos); los pacientes —facturables por la
  // facturación especializada— quedan después y salen al escribir su nombre o cédula.
  const esCliente = (p: PartnerOpt) => (p.tipo_partner || []).includes('cliente');
  const itemsCliente = useMemo(() => [...clientes]
    .sort((a, b) => Number(esCliente(b)) - Number(esCliente(a)) || a.razon_social.localeCompare(b.razon_social, 'es'))
    .map((p) => ({ id: p.id, codigo: p.identificacion ?? '', nombre: p.razon_social, tipo: esCliente(p) ? 'Cliente' : 'Paciente' })), [clientes]);
  const { modo: modoCentro } = useCentroCostoModo();
  const { data: centrosContables = [] } = useCentrosContables(modoCentro === 'contable');
  const labelCentro = labelCentroCosto(modoCentro);
  const itemsCentro = useMemo(() => (modoCentro === 'contable'
    ? centrosContables.map((c) => ({ id: c.id, codigo: c.codigo ?? '', nombre: c.nombre }))
    : centros.map((p) => ({ id: p.id, codigo: p.identificacion ?? '', nombre: p.razon_social }))), [modoCentro, centrosContables, centros]);
  const { data: categorias = [] } = useQuery<string[]>({
    queryKey: ['sri-categorias-emitidos'], queryFn: async () => (await apiClient.post('/rpc/sri_categorias_emitidos', {}, FIN)).data || [],
  });
  // Solo productos tipo servicio (facturación por servicios)
  const { data: productos = [] } = useQuery<ProductoOpt[]>({
    queryKey: ['fac-catalogo'],
    queryFn: async () => (await apiClient.post('/rpc/fac_catalogo_facturable', { _busqueda: null },
      { headers: { 'Content-Profile': 'financiero', 'Accept-Profile': 'financiero' } })).data || [],
  });

  const [clienteId, setClienteId] = useState('');
  const [facReembolsoId, setFacReembolsoId] = useState('');
  const [proyectoId, setProyectoId] = useState('');
  const [centroId, setCentroId] = useState('');
  const [categoria, setCategoria] = useState('');
  const [pagos, setPagos] = useState<PagoCab[]>([{ forma_pago: '01', total: 0, plazo: 0 }]);
  const [obs, setObs] = useState('');
  const [lineas, setLineas] = useState<Linea[]>([nuevaLinea()]);

  // Modo edición: cargar el comprobante y precargar el formulario
  const { data: edit } = useQuery({
    queryKey: ['fac-obtener', editId], enabled: !!editId,
    queryFn: async () => (await apiClient.post('/rpc/fac_obtener', { _ticket_id: editId }, FIN)).data,
  });
  useEffect(() => {
    if (!edit) return;
    setClienteId(edit.partner_id || ''); setProyectoId(edit.proyecto_id || '');
    setCentroId((modoCentro === 'contable' ? edit.cc_contable_id : edit.centro_costo_id) || '');
    setCategoria(edit.categoria || '');
    setPagos([{ forma_pago: edit.forma_pago || '01', total: Number(edit.importe_total) || 0, plazo: Number(edit.plazo_dias) || 0 }]);
    setObs(edit.observaciones || '');
    // La línea guarda el `codigo` del producto; el combo se indexa por id → hay que
    // resolver el producto_id por código para que el <select> preseleccione (y para que
    // el guardado no descarte la línea por producto_id vacío). Si los productos aún no
    // cargaron, el efecto de backfill de abajo lo completa al llegar la lista.
    setLineas((edit.lineas || []).map((l: { codigo?: string; descripcion?: string; cantidad?: number; precio_unitario?: number; descuento?: number; iva_tarifa?: number; proyecto_id?: string }) => ({
      key: nuevaLinea().key, producto_id: productos.find((p) => p.codigo === l.codigo)?.id || '', codigo: l.codigo || '', descripcion: l.descripcion || '',
      cantidad: Number(l.cantidad) || 0, precio_unitario: Number(l.precio_unitario) || 0,
      descMode: 'fijo' as const, descVal: Number(l.descuento) || 0, iva_tarifa: Number(l.iva_tarifa) || 0, proyecto_id: l.proyecto_id || '',
    })));
  }, [edit, modoCentro]); // eslint-disable-line react-hooks/exhaustive-deps -- no depende de `productos` para no pisar ediciones al refetchear

  // Backfill: cuando cargan los productos (o refetchean), completa el producto_id de las
  // líneas que quedaron sin resolver por código. Solo toca líneas con producto_id vacío,
  // así no pisa lo que el usuario ya editó.
  useEffect(() => {
    if (!productos.length) return;
    setLineas((ls) => ls.map((l) => (!l.producto_id && l.codigo ? { ...l, producto_id: productos.find((p) => p.codigo === l.codigo)?.id || '' } : l)));
  }, [productos]);

  const setLinea = (key: number, patch: Partial<Linea>) => setLineas((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  // Vínculo con factura de REEMBOLSO (726): combo del mismo cliente + % de honorarios
  interface FacReemb { id: string; serie: string | null; fecha: string | null; importe_total: number; total_reembolsos: number }
  const { data: reembInfo } = useQuery<{ pct: number; vinculada: string | null; facturas: FacReemb[] }>({
    queryKey: ['fe-fact-con-reembolso', clienteId, editId ?? null],
    enabled: !!clienteId,
    queryFn: async () => (await apiClient.post('/rpc/fe_facturas_con_reembolso', { _cliente: clienteId, _actual: editId || null }, FIN)).data,
  });
  useEffect(() => { setFacReembolsoId(reembInfo?.vinculada ?? ''); }, [reembInfo?.vinculada, clienteId]);
  const elegirFacReembolso = (id: string) => {
    setFacReembolsoId(id);
    if (!id) return;
    const f = (reembInfo?.facturas ?? []).find((x) => x.id === id);
    const pct = Number(reembInfo?.pct ?? 50);
    if (!f) return;
    const precio = Math.round(Number(f.total_reembolsos) * pct) / 100;  // (total×pct/100) redondeado a 2 dec
    setLineas((ls) => ls.map((l, i) => (i === 0 ? { ...l, precio_unitario: precio } : l)));
    toast.info(`Primer ítem precargado: ${pct}% de ${Number(f.total_reembolsos).toFixed(2)} = ${precio.toFixed(2)} (editable)`);
  };

  // Al elegir producto: precio e IVA por defecto vía resolver_precio (según cliente/convenio)
  const elegirProducto = async (key: number, pid: string) => {
    const p = productos.find((x) => x.id === pid);
    if (!p) { setLinea(key, { producto_id: '', codigo: '', descripcion: '' }); return; }
    setLinea(key, { producto_id: pid, codigo: p.codigo, descripcion: p.nombre, iva_tarifa: Number(p.iva_tarifa), precio_unitario: Number(p.precio_base) });
    // Un examen no está en listas de precios ni en convenios: su tarifa es la
    // del catálogo médico. Preguntarle a `resolver_precio` por un id que no es
    // de inventario sólo puede devolver ruido.
    if (p.origen === 'examen') return;
    try {
      const r = (await apiClient.post('/rpc/resolver_precio',
        { _producto_id: pid, _partner_id: clienteId || null, _fecha: hoy, _sentido: 'venta' }, INV)).data;
      if (r) setLinea(key, { precio_unitario: Number(r.precio) || Number(p.precio_base), iva_tarifa: r.iva_tarifa != null ? Number(r.iva_tarifa) : Number(p.iva_tarifa) });
    } catch { /* deja el precio_base */ }
  };

  const totSub = lineas.reduce((s, l) => s + baseLinea(l), 0);
  const totDesc = lineas.reduce((s, l) => s + l.cantidad * descUnit(l), 0);
  const totIva = lineas.reduce((s, l) => s + baseLinea(l) * (l.iva_tarifa || 0) / 100, 0);
  const totTotal = totSub + totIva;

  const crear = useMutation({
    mutationFn: async () => {
      // línea válida = producto del catálogo O línea libre (rubro con descripción)
      const items = lineas.filter((l) => (l.producto_id || l.descripcion.trim()) && l.cantidad > 0).map((l) => ({
        codigo: l.codigo, descripcion: l.descripcion, cantidad: l.cantidad, precio_unitario: l.precio_unitario,
        descuento: Number(descUnit(l).toFixed(6)), iva_tarifa: l.iva_tarifa, proyecto_id: l.proyecto_id || proyectoId || null,
        centro_costo_id: modoCentro === 'contable' ? null : (centroId || null),
        cc_contable_id: modoCentro === 'contable' ? (centroId || null) : null,
      }));
      if (items.length === 0) throw new Error('Agrega al menos una línea con producto (o descripción) y cantidad');
      if (!centroId) throw new Error('Selecciona el centro de costo');
      const pagosValidos = pagos.filter((p) => Number(p.total) > 0);
      if (!editId && pagosValidos.length > 0) {
        const sumaPagos = Math.round(pagosValidos.reduce((s, p) => s + Number(p.total), 0) * 100) / 100;
        if (Math.abs(sumaPagos - Math.round(totTotal * 100) / 100) > 0.01) {
          throw new Error(`Las formas de pago (${sumaPagos.toFixed(2)}) deben sumar el total (${totTotal.toFixed(2)})`);
        }
      }
      const cab = {
        cliente_partner_id: clienteId, proyecto_id: proyectoId || null,
        centro_costo_id: modoCentro === 'contable' ? null : (centroId || null),
        cc_contable_id: modoCentro === 'contable' ? (centroId || null) : null,
        categoria: categoria || null,
        forma_pago: pagos[0]?.forma_pago || '01', plazo_dias: pagos[0]?.plazo || 0,
        pagos: pagosValidos.map((p) => ({ forma_pago: p.forma_pago, total: p.total, plazo: p.plazo, unidad_tiempo: p.plazo > 0 ? 'dias' : null })),
        observaciones: obs || null,
      };
      const r = editId
        ? (await apiClient.post('/rpc/fac_actualizar', { _ticket_id: editId, _cab: cab, _items: items }, FIN)).data
        : (await apiClient.post('/rpc/fac_crear_factura', { _cab: cab, _items: items }, FIN)).data;
      // trazabilidad: vincular (o desvincular) la factura de reembolso elegida
      const tid = editId || r?.ticket_id;
      if (tid && (facReembolsoId || reembInfo?.vinculada)) {
        await apiClient.post('/rpc/fe_factura_reembolso_vincular', { _ticket: tid, _origen: facReembolsoId || null }, FIN);
      }
      return r;
    },
    onSuccess: () => { toast.success(editId ? 'Factura actualizada' : 'Factura creada'); onCreada(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Datos de cabecera</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-muted-foreground">Cliente (RUC o razón social) *</label>
            <BuscadorCombo value={clienteId} onChange={setClienteId} items={itemsCliente} compact allowClear pista={(i) => i.tipo}
              placeholder="Escriba el RUC, la cédula o la razón social…" />
            {clienteId && (reembInfo?.facturas ?? []).length + (facReembolsoId ? 1 : 0) > 0 && (
              <div className="mt-2">
                <label className="text-xs font-medium text-muted-foreground"
                       title="Trazabilidad reembolso ↔ servicios: al elegirla, el precio del primer ítem se precarga con el % configurado sobre la suma de reembolsos">
                  Factura de reembolso vinculada ({Number(reembInfo?.pct ?? 50)}% al primer ítem)
                </label>
                <select className={field} value={facReembolsoId} onChange={(e) => elegirFacReembolso(e.target.value)}>
                  <option value="">— Sin vínculo —</option>
                  {(reembInfo?.facturas ?? []).map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.serie ?? '(sin serie)'} · {String(f.fecha ?? '').slice(0, 10)} · reembolsos {Number(f.total_reembolsos).toFixed(2)}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Fecha de emisión</label>
            <input className={field} value={hoy} readOnly disabled />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Proyecto</label>
            {/* escribir filtra (por palabras, sin tildes, nombre o identificación), como los productos del POS */}
            <BuscadorCombo value={proyectoId} onChange={setProyectoId} items={itemsProyecto} compact allowClear
              placeholder="Escriba para buscar el proyecto…" />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">{labelCentro} *</label>
            <BuscadorCombo value={centroId} onChange={setCentroId} items={itemsCentro} compact allowClear
              placeholder={`Escriba para buscar el ${labelCentro.toLowerCase()}…`} />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Categoría</label>
            <select className={field} value={categoria} onChange={(e) => setCategoria(e.target.value)}>
              <option value="">— (se define al procesar) —</option>
              {categorias.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <PagosEditor pagos={pagos} setPagos={setPagos} total={totTotal} />
          <div className="md:col-span-3">
            <label className="text-xs font-medium text-muted-foreground">Observación</label>
            <input className={field} value={obs} onChange={(e) => setObs(e.target.value)} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2 flex-row items-center justify-between">
          <CardTitle className="text-base">Detalle</CardTitle>
          <Button size="sm" variant="outline" onClick={() => setLineas((l) => [...l, nuevaLinea(proyectoId)])}><Plus className="w-4 h-4 mr-1" /> Línea</Button>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-1 pr-2 min-w-48">Producto</th><th className="py-1 pr-2">Cant.</th><th className="py-1 pr-2">P. Unit.</th>
              <th className="py-1 pr-2">Desc.</th><th className="py-1 pr-2">IVA%</th><th className="py-1 pr-2 text-right">Subtotal</th>
              <th className="py-1 pr-2 text-right">Total</th><th className="py-1 pr-2">Proyecto</th><th></th>
            </tr></thead>
            <tbody>
              {lineas.map((l) => (
                <tr key={l.key} className="border-b last:border-0">
                  <td className="py-1 pr-2">
                    {!l.producto_id && l.descripcion ? (
                      <div className="flex items-center gap-1">
                        <input className={field} value={l.descripcion} title="Línea libre (rubro): sin producto del catálogo — edita la descripción directamente"
                          onChange={(e) => setLinea(l.key, { descripcion: e.target.value })} />
                        <span className="rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-medium text-sky-700 dark:bg-sky-900/30 dark:text-sky-300 whitespace-nowrap" title="Esta línea no está atada a un producto del catálogo (rubro de facturación especializada)">rubro</span>
                      </div>
                    ) : (
                      // Mismo buscador que la recepción de inventario: se teclea por
                      // código o por palabras del nombre (sin tildes) y el desplegable
                      // sale en un portal para no quedar recortado por la celda. Con
                      // 300+ exámenes en el catálogo, una lista desplegable no se usa.
                      <BuscadorCombo
                        items={productos} value={l.producto_id}
                        onChange={(id) => void elegirProducto(l.key, id)}
                        placeholder="Buscar producto o examen…"
                        pista={(p) => p.grupo} compact allowClear
                      />
                    )}
                  </td>
                  <td className="py-1 pr-2"><input type="number" step="0.01" className={`${field} w-20`} value={l.cantidad} onChange={(e) => setLinea(l.key, { cantidad: Number(e.target.value) })} /></td>
                  <td className="py-1 pr-2"><input type="number" step="0.0001" className={`${field} w-24`} value={l.precio_unitario} onChange={(e) => setLinea(l.key, { precio_unitario: Number(e.target.value) })} /></td>
                  <td className="py-1 pr-2">
                    <div className="flex gap-1">
                      <input type="number" step="0.01" className={`${field} w-16`} value={l.descVal} onChange={(e) => setLinea(l.key, { descVal: Number(e.target.value) })} />
                      <select className={`${field} w-14`} value={l.descMode} onChange={(e) => setLinea(l.key, { descMode: e.target.value as 'pct' | 'fijo' })}>
                        <option value="pct">%</option><option value="fijo">$</option>
                      </select>
                    </div>
                  </td>
                  <td className="py-1 pr-2 text-muted-foreground whitespace-nowrap" title="IVA del producto (no editable)">{Number(l.iva_tarifa)}%</td>
                  <td className="py-1 pr-2 text-right whitespace-nowrap">{usd(baseLinea(l))}</td>
                  <td className="py-1 pr-2 text-right whitespace-nowrap font-medium">{usd(totalLinea(l))}</td>
                  <td className="py-1 pr-2">
                    <div className="w-44">
                      <BuscadorCombo value={l.proyecto_id} onChange={(id) => setLinea(l.key, { proyecto_id: id })} items={itemsProyecto} compact allowClear
                        placeholder={proyectoId ? '(cabecera)' : 'Buscar…'} />
                    </div>
                  </td>
                  <td className="py-1"><button className="text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded p-1" onClick={() => setLineas((ls) => ls.filter((x) => x.key !== l.key))}><Trash2 className="w-4 h-4" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-3 flex justify-end">
            <div className="w-64 space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{usd(totSub)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Descuento</span><span>{usd(totDesc)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">IVA</span><span>{usd(totIva)}</span></div>
              <div className="flex justify-between border-t pt-1 font-semibold"><span>Total</span><span>{usd(totTotal)}</span></div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        {editId && onCancelar && (
          <Button variant="outline" onClick={onCancelar}>Cancelar</Button>
        )}
        <Button isLoading={crear.isPending} disabled={!clienteId || !centroId} onClick={() => crear.mutate()}>{editId ? 'Guardar cambios' : 'Generar factura'}</Button>
      </div>
    </div>
  );
}

interface FacturaRow {
  id: string; origen?: string; fecha_emision: string; cliente: string | null; ruc_receptor?: string | null; importe_total: number; lineas: number;
  estado_emision: string; mensaje_sri?: string | null; serie_comprobante?: string | null; xml_url?: string | null; ride_url?: string | null;
  proyecto?: string | null; centro_costo?: string | null;
  // 971: reembolso de gastos adjunto y vínculos con la factura de servicios
  updated_at?: string; retencion_sobre_reembolso?: boolean;
  reembolsos?: number; total_reembolso?: number; reembolso_vinculado?: string | null; servicios_vinculada?: string | null;
}

// Filtro «Reembolso» de la pestaña Facturas
const FILTRO_REEMB: { v: string; t: string }[] = [
  { v: '', t: 'Todas' },
  { v: 'con', t: 'Con reembolso de gastos' },
  { v: 'sin', t: 'Sin reembolso' },
  { v: 'vinculada', t: 'Servicios vinculadas a un reembolso' },
  { v: 'pendiente_vinculo', t: 'Reembolsos autorizados sin factura de servicios' },
];
const esReemb = (d: FacturaRow) => Number(d.reembolsos ?? 0) > 0;

function Emitidas({ onEdit }: { onEdit: (id: string) => void }) {
  const qc = useQueryClient();
  const [descartar, setDescartar] = useState<DocDescartable | null>(null);
  // Anular lo ya autorizado (portal SRI / NC): solo nivel 5, igual que la RPC.
  const [anular, setAnular] = useState<DocAnulable | null>(null);
  const { isAdmin } = usePermissions();
  const { data: docs = [], isLoading, isFetching } = useQuery<FacturaRow[]>({
    queryKey: ['fac-facturas'], queryFn: async () => (await apiClient.post('/rpc/fac_facturas', { _estado: 'todos' }, FIN)).data || [],
    // Se reconsulta solo mientras una emisión AVANZA (movida en los últimos 5 min):
    // una factura atascada en «procesando» refrescaba la lista cada 2,5 s sin fin.
    refetchInterval: (q) => {
      const rows = (q.state.data || []) as FacturaRow[];
      const reciente = (d: FacturaRow) => !d.updated_at || Date.now() - new Date(d.updated_at).getTime() < 5 * 60 * 1000;
      if (rows.some((d) => ['encolado', 'procesando'].includes(d.estado_emision) && reciente(d))) return 2500;
      if (rows.some((d) => d.estado_emision === 'en_procesamiento' && reciente(d))) return 10000; // reconsulta del SRI (celcer puede tardar)
      if (rows.some((d) => ['encolado', 'procesando', 'en_procesamiento'].includes(d.estado_emision))) return 60000; // atascada: 1 vez por minuto
      return false;
    },
  });

  const emitir = useMutation({
    mutationFn: async (id: string) => (await apiClient.post('/rpc/fe_emitir', { _ticket_id: id }, FIN)).data,
    onSuccess: () => { toast.success('Factura encolada para emisión'); void qc.invalidateQueries({ queryKey: ['fac-facturas'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [reembolsoId, setReembolsoId] = useState<string | null>(null);
  const [cli, setCli] = useState('');
  const [proy, setProy] = useState('');
  const [cc, setCc] = useState('');
  const [reemb, setReemb] = useState('');
  const [est, setEst] = useState('');
  const clientes = useMemo(() => ([...new Set(docs.map((d) => d.cliente).filter(Boolean))] as string[]).sort(), [docs]);
  const proyectos = useMemo(() => ([...new Set(docs.map((d) => d.proyecto).filter(Boolean))] as string[]).sort(), [docs]);
  const centros = useMemo(() => ([...new Set(docs.map((d) => d.centro_costo).filter(Boolean))] as string[]).sort(), [docs]);
  const filtradas = useMemo(() => docs.filter((d) =>
    (!desde || d.fecha_emision >= desde) && (!hasta || d.fecha_emision <= hasta) && (!cli || d.cliente === cli)
    && (!proy || d.proyecto === proy) && (!cc || d.centro_costo === cc)
    && cumpleEstado(d.estado_emision, est)
    && (!reemb
      || (reemb === 'con' && esReemb(d))
      || (reemb === 'sin' && !esReemb(d))
      || (reemb === 'vinculada' && !!d.reembolso_vinculado)
      || (reemb === 'pendiente_vinculo' && esReemb(d) && d.estado_emision === 'autorizado' && !d.servicios_vinculada))
  ), [docs, desde, hasta, cli, proy, cc, reemb, est]);
  // cantidad por estado (sobre todas las facturas) para el desplegable
  const porEstado = useMemo(() => docs.reduce<Record<string, number>>((a, d) => { a[d.estado_emision] = (a[d.estado_emision] ?? 0) + 1; return a; }, {}), [docs]);
  const totalReembFiltrado = filtradas.reduce((a, d) => a + Number(d.total_reembolso ?? 0), 0);
  // 974: cuadre de la cuenta «Reembolsos de gastos por cobrar» (se muestra al filtrar reembolsos)
  const { data: cuadreReemb } = useQuery<{ configurada: boolean; cuenta?: string; saldo?: number; por_facturar?: number; compras?: number; dias_mas_antigua?: number; diferencia?: number; cuadra?: boolean }>({
    queryKey: ['fe-reembolso-cuadre'], queryFn: async () => (await apiClient.post('/rpc/fe_reembolso_cuadre', {}, FIN)).data,
    enabled: !!reemb && reemb !== 'sin',
  });

  // Ordenamiento por columna (Fecha / Cliente / Total). Clic alterna asc↔desc.
  const [orden, setOrden] = useState<{ col: 'fecha' | 'cliente' | 'total'; dir: 'asc' | 'desc' }>({ col: 'fecha', dir: 'desc' });
  const ordenar = (col: 'fecha' | 'cliente' | 'total') =>
    setOrden((o) => (o.col === col ? { col, dir: o.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' }));
  const flecha = (col: 'fecha' | 'cliente' | 'total') => (orden.col === col ? (orden.dir === 'asc' ? ' ▲' : ' ▼') : '');
  const ordenadas = useMemo(() => {
    const arr = [...filtradas];
    const s = orden.dir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      if (orden.col === 'total') return (Number(a.importe_total) - Number(b.importe_total)) * s;
      if (orden.col === 'cliente') return (a.cliente || '').localeCompare(b.cliente || '', 'es') * s;
      return String(a.fecha_emision).localeCompare(String(b.fecha_emision)) * s; // fecha (YYYY-MM-DD)
    });
    return arr;
  }, [filtradas, orden]);

  const dlg = useDialogoEnLinea();

  return (
    <Card>
      {dlg.ui}
      <CardHeader className="pb-2">
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <CardTitle className="text-base">Facturas ({filtradas.length})
            {isFetching && !isLoading && <Loader2 className="ml-2 inline h-3.5 w-3.5 animate-spin text-muted-foreground" aria-label="Actualizando" />}
            {reemb && reemb !== 'sin' && totalReembFiltrado > 0 && <span className="ml-2 text-xs font-normal text-muted-foreground">· reembolsos {usd(totalReembFiltrado)}</span>}
          </CardTitle>
          <div className="flex items-end gap-2 flex-wrap text-xs text-muted-foreground">
            <label className="flex flex-col">Desde
              <input type="date" className="h-9 mt-1 rounded-md border border-input bg-background px-2 text-sm" value={desde} onChange={(e) => setDesde(e.target.value)} /></label>
            <label className="flex flex-col">Hasta
              <input type="date" className="h-9 mt-1 rounded-md border border-input bg-background px-2 text-sm" value={hasta} onChange={(e) => setHasta(e.target.value)} /></label>
            <label className="flex flex-col">Cliente
              <select className="h-9 mt-1 max-w-56 rounded-md border border-input bg-background px-2 text-sm" value={cli} onChange={(e) => setCli(e.target.value)}>
                <option value="">Todos</option>
                {clientes.map((c) => <option key={c} value={c}>{c}</option>)}
              </select></label>
            <label className="flex flex-col">Proyecto
              <select className="h-9 mt-1 max-w-56 rounded-md border border-input bg-background px-2 text-sm" value={proy} onChange={(e) => setProy(e.target.value)}>
                <option value="">Todos</option>
                {proyectos.map((p) => <option key={p} value={p}>{p}</option>)}
              </select></label>
            <label className="flex flex-col">Centro de costo
              <select className="h-9 mt-1 max-w-56 rounded-md border border-input bg-background px-2 text-sm" value={cc} onChange={(e) => setCc(e.target.value)}>
                <option value="">Todos</option>
                {centros.map((c) => <option key={c} value={c}>{c}</option>)}
              </select></label>
            <label className="flex flex-col">Estado
              <select className="h-9 mt-1 max-w-64 rounded-md border border-input bg-background px-2 text-sm" value={est} onChange={(e) => setEst(e.target.value)} aria-label="Filtrar por estado">
                <option value="">Todos</option>
                <optgroup label="Grupos">
                  {Object.entries(GRUPOS_ESTADO).map(([k, g]) => (
                    <option key={k} value={k}>{g.t} ({g.estados.reduce((a, e) => a + (porEstado[e] ?? 0), 0)})</option>
                  ))}
                </optgroup>
                <optgroup label="Estado">
                  {Object.entries(ESTADO).filter(([k]) => porEstado[k]).map(([k, v]) => <option key={k} value={k}>{v.label} ({porEstado[k]})</option>)}
                </optgroup>
              </select></label>
            <label className="flex flex-col">Reembolso
              <select className="h-9 mt-1 max-w-64 rounded-md border border-input bg-background px-2 text-sm" value={reemb} onChange={(e) => setReemb(e.target.value)} aria-label="Filtrar por reembolso">
                {FILTRO_REEMB.map((o) => <option key={o.v} value={o.v}>{o.t}</option>)}
              </select></label>
            {(desde || hasta || cli || proy || cc || reemb || est) && <Button size="sm" variant="outline" onClick={() => { setDesde(''); setHasta(''); setCli(''); setProy(''); setCc(''); setReemb(''); setEst(''); }}>Limpiar</Button>}
          </div>
        </div>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        {reemb && reemb !== 'sin' && cuadreReemb?.configurada && (
          <p className={`mb-2 rounded border px-2 py-1.5 text-xs ${cuadreReemb.cuadra ? 'border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-200' : 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200'}`}>
            Cuenta {cuadreReemb.cuenta} «Reembolsos de gastos por cobrar»: saldo contable {usd(Number(cuadreReemb.saldo))} · compras adjuntas aún sin factura autorizada {usd(Number(cuadreReemb.por_facturar))} ({cuadreReemb.compras}{Number(cuadreReemb.compras) > 0 ? `, la más antigua de ${cuadreReemb.dias_mas_antigua} días` : ''}) · {cuadreReemb.cuadra ? '✓ cuadra' : `⚠ diferencia ${usd(Number(cuadreReemb.diferencia))}: revise la bandeja de excepciones contables`}
          </p>
        )}
        {/* el spinner grande solo en la primera carga; las reconsultas de fondo no mueven la tabla */}
        {isLoading && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
        <table className="w-full text-sm">
          <thead><tr className="border-b text-left text-xs text-muted-foreground">
            <th className="py-2 pr-3"><button type="button" className="hover:text-foreground" onClick={() => ordenar('fecha')}>Fecha{flecha('fecha')}</button></th>
            <th className="py-2 pr-3">N° Documento</th>
            <th className="py-2 pr-3"><button type="button" className="hover:text-foreground" onClick={() => ordenar('cliente')}>Cliente{flecha('cliente')}</button></th>
            <th className="py-2 pr-3 text-right">Líneas</th>
            <th className="py-2 pr-3 text-right"><button type="button" className="hover:text-foreground" onClick={() => ordenar('total')}>Total{flecha('total')}</button></th>
            <th className="py-2 pr-3">Estado</th><th className="py-2"></th>
          </tr></thead>
          <tbody>
            {filtradas.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Sin facturas</td></tr>}
            {ordenadas.map((d) => {
              const extra = [
                d.proyecto && `Proyecto: ${d.proyecto}`, d.centro_costo && `Centro de costo: ${d.centro_costo}`,
                esReemb(d) && `Reembolso: ${d.reembolsos} comprobante(s) · ${usd(Number(d.total_reembolso ?? 0))}`,
                esReemb(d) && d.servicios_vinculada && `Honorarios en ${d.servicios_vinculada}`,
                d.reembolso_vinculado && `Vinculada al reembolso ${d.reembolso_vinculado}`,
              ].filter(Boolean).join('  ·  ');
              return (
              <Fragment key={d.id}>
              <tr className={`hover:bg-muted/40 ${extra ? '' : 'border-b last:border-0'}`}>
                <td className="py-2 pr-3 whitespace-nowrap">{d.fecha_emision}</td>
                <td className="py-2 pr-3 font-mono text-xs whitespace-nowrap">{d.serie_comprobante || <span className="italic text-muted-foreground">Sin emitir</span>}
                  {esReemb(d) && <span className="ml-1.5 rounded-full bg-amber-100 px-1.5 py-0.5 font-sans text-[10px] text-amber-800 dark:bg-amber-900/40 dark:text-amber-300" title="Factura con reembolso de gastos (cód. 41)">Reembolso</span>}
                  {d.retencion_sobre_reembolso && <span className="ml-1.5 rounded-full bg-red-100 px-1.5 py-0.5 font-sans text-[10px] text-red-800 dark:bg-red-900/40 dark:text-red-300" title="El cliente retuvo sobre el reembolso de gastos: solo debe retener sobre lo propio (honorario). Pídale que anule y reemita la retención.">Retención indebida</span>}
                  {d.reembolso_vinculado && <span className="ml-1.5 rounded-full bg-sky-100 px-1.5 py-0.5 font-sans text-[10px] text-sky-800 dark:bg-sky-900/40 dark:text-sky-300" title={`Factura de servicios vinculada al reembolso ${d.reembolso_vinculado}`}>Vinculada</span>}
                </td>
                <td className="py-2 pr-3"><span className="truncate max-w-64 inline-block align-middle">{d.cliente || '—'}</span></td>
                <td className="py-2 pr-3 text-right">{d.lineas}</td>
                <td className="py-2 pr-3 text-right whitespace-nowrap font-medium">{usd(Number(d.importe_total))}</td>
                <td className="py-2 pr-3">
                  <span className={`rounded-full px-2 py-0.5 text-xs ${(ESTADO[d.estado_emision] || ESTADO.por_emitir).cls}`}>{(ESTADO[d.estado_emision] || ESTADO.por_emitir).label}</span>
                  {['devuelto', 'rechazado', 'error'].includes(d.estado_emision) && d.mensaje_sri && (
                    <button type="button" onClick={() => void dlg.avisar('Mensaje del SRI', d.mensaje_sri!)} title="Ver motivo completo"
                      className="mt-1 block max-w-xs text-left text-xs text-red-600 hover:underline">
                      ⚠ {d.mensaje_sri.length > 140 ? d.mensaje_sri.slice(0, 140) + '…' : d.mensaje_sri}
                    </button>
                  )}
                </td>
                <td className="py-2 text-right whitespace-nowrap">
                  <button className="inline-flex items-center gap-1 mr-2 text-xs text-primary hover:underline align-middle"
                    title={d.estado_emision === 'autorizado' ? 'Ver la factura en PDF' : 'Ver PDF en borrador (proforma — sin validez tributaria hasta emitirse)'}
                    onClick={() => { void abrirFacturaPdf(d).catch((e) => toast.error(e?.message || 'No se pudo abrir el PDF')); }}>
                    <FileText className="w-4 h-4" /> PDF
                  </button>
                  {urlDoc(d.ride_url) && <a href={urlDoc(d.ride_url)!} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mr-2 text-xs text-primary hover:underline align-middle"><FileText className="w-4 h-4" /> RIDE</a>}
                  {urlDoc(d.xml_url) && <a href={urlDoc(d.xml_url)!} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mr-2 text-xs text-primary hover:underline align-middle"><Download className="w-4 h-4" /> XML</a>}
                  {EMITIBLE.includes(d.estado_emision) && <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted mr-1 align-middle" title="Editar" onClick={() => onEdit(d.id)}><Pencil className="w-4 h-4" /></button>}
                  {EMITIBLE.includes(d.estado_emision) && <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted mr-1 align-middle" title="Reembolso de gastos (seleccionar recibidos)" onClick={() => setReembolsoId(d.id)}><Receipt className="w-4 h-4" /></button>}
                  {EMITIBLE.includes(d.estado_emision) && <Button size="sm" isLoading={emitir.isPending} onClick={() => emitir.mutate(d.id)}>Emitir</Button>}
                  {['encolado', 'procesando'].includes(d.estado_emision) && <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><Loader2 className="w-3 h-3 animate-spin" /> procesando…</span>}
                  {d.estado_emision === 'en_procesamiento' && <span className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400" title={d.mensaje_sri || ''}><Loader2 className="w-3 h-3 animate-spin" /> en cola del SRI…</span>}
                  {EMITIBLE.includes(d.estado_emision) && <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1 align-middle" title="Descartar" onClick={() => setDescartar({ id: d.id, serie_comprobante: d.serie_comprobante, estado_emision: d.estado_emision })}><Trash2 className="w-4 h-4" /></button>}
                  {d.estado_emision === 'autorizado' && isAdmin() && <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1 align-middle" title="Anular (ya anulada en el portal del SRI o con nota de crédito)" onClick={() => setAnular({ id: d.id, serie_comprobante: d.serie_comprobante, cliente: d.cliente, importe_total: d.importe_total })}><Ban className="w-4 h-4" /></button>}
                </td>
              </tr>
              {extra && (
                <tr className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-0"></td>
                  <td colSpan={6} className="pb-2 pt-0 text-[11px] text-muted-foreground/80">{extra}</td>
                </tr>
              )}
              </Fragment>
              );
            })}
          </tbody>
        </table>
        {reembolsoId && <ReembolsoModal ticketId={reembolsoId} onClose={() => setReembolsoId(null)} />}
        {descartar && <DescartarDialog doc={descartar} onClose={() => setDescartar(null)}
          onDone={() => void qc.invalidateQueries({ queryKey: ['fac-facturas'] })} />}
        {anular && <AnularDialog doc={anular} onClose={() => setAnular(null)}
          onDone={() => void qc.invalidateQueries()} />}
      </CardContent>
    </Card>
  );
}
